import {
  OrderTicketItemStatusUpdatedEvent,
  OrderTicketStatusUpdatedEvent,
} from '@ancon/wildcat-types'
import { PayloadAction } from '@reduxjs/toolkit'
import {
  CheckoutStatus,
  CheckoutStatusUpdatedEvent,
  CustomerCheckoutListItem,
} from '@ancon/wildcat-types/dist/checkout'

import { OrderReducerState } from '../types'
import getTicketItemStatusOfTicketStatus from '../utils/getTicketItemStatusOfTicketStatus'
import updateTicketItemsStatus from '../utils/updateTicketItemsStatus'
import updateTicketStatusFromItemsStatus from '../utils/updateTicketStatusFromItemsStatus'

import {
  activeOrdersEntityAdapter,
  checkOrderTicketsEntityAdapter,
} from './ordersEntityAdapter'

const checkOrderTicketsEntityAdapterSelectors =
  checkOrderTicketsEntityAdapter.getSelectors()

const checkoutActiveOrdersEntityAdapterSelectors =
  activeOrdersEntityAdapter.getSelectors()

const ordersReducers = {
  ordersClearOrderDetails(state: OrderReducerState) {
    state.orderDetails = null
  },
  customerOrdersEmitCheckoutStatusEvent(
    state: OrderReducerState,
    action: PayloadAction<CheckoutStatusUpdatedEvent>,
  ) {
    const { checkoutId, orderId, status } = action.payload
    const checkoutDetails =
      checkoutActiveOrdersEntityAdapterSelectors.selectById(
        state.activeOrders,
        checkoutId,
      )
    if (checkoutDetails && !Number.isNaN(status)) {
      if (status === CheckoutStatus.Done) {
        activeOrdersEntityAdapter.removeOne(state.activeOrders, checkoutId)
      } else {
        activeOrdersEntityAdapter.updateOne(state.activeOrders, {
          id: checkoutId,
          changes: {
            status,
            orderId,
          },
        })
      }
    }
  },
  customerOrdersEmitTicketItemStatusEvent(
    state: OrderReducerState,
    action: PayloadAction<OrderTicketItemStatusUpdatedEvent>,
  ) {
    const { orderId, ticketItemId, ticketItemStatus } = action.payload

    const checkout =
      state.activeOrders.ids.reduce<CustomerCheckoutListItem | null>(
        (acc, checkoutId) => {
          const checkoutEntity = state.activeOrders.entities[checkoutId]
          if (checkoutEntity && checkoutEntity.orderId === orderId) {
            return checkoutEntity
          }
          return acc
        },
        null,
      )

    if (checkout) {
      const updatedAttendees = checkout.attendees?.map(attendee => {
        const attendeeItems = attendee.items ?? []
        const updatedItems = attendeeItems.map(item => {
          const { ticketItem } = item
          if (
            ticketItem &&
            ticketItem.ticketItemId === ticketItemId &&
            ticketItem.status < ticketItemStatus
          ) {
            item.ticketItem!.status = ticketItemStatus
          }
          return item
        })

        return { ...attendee, items: updatedItems }
      })

      activeOrdersEntityAdapter.updateOne(state.activeOrders, {
        id: checkout.id,
        changes: {
          attendees: updatedAttendees,
        },
      })
    }
  },
  checkOrderEmitTicketStatusUpdatedEvent(
    state: OrderReducerState,
    action: PayloadAction<OrderTicketStatusUpdatedEvent>,
  ) {
    if (
      state.checkOrderSummary &&
      state.checkOrderSummary.id === action.payload.orderId
    ) {
      const { ticketIds, ticketStatus } = action.payload

      // STEP 1: Update ticket statuses
      const updates = ticketIds.map(ticketId => ({
        id: ticketId,
        changes: {
          status: ticketStatus,
        },
      }))

      checkOrderTicketsEntityAdapter.updateMany(
        state.checkOrderSummaryTickets,
        updates,
      )

      // STEP 2: Update ticket items statuses of the tickets
      const ticketItemStatus = getTicketItemStatusOfTicketStatus(ticketStatus)

      ticketIds.forEach(ticketId => {
        const ticket = checkOrderTicketsEntityAdapterSelectors.selectById(
          state.checkOrderSummaryTickets,
          ticketId,
        )

        const ticketItemIds = ticket?.ticketItems.map(item => item.id) || []

        updateTicketItemsStatus(
          state,
          ticketId,
          ticketItemIds,
          ticketItemStatus,
        )
      })
    }
  },
  checkOrderEmitTicketItemStatusUpdatedEvent(
    state: OrderReducerState,
    action: PayloadAction<OrderTicketItemStatusUpdatedEvent>,
  ) {
    if (
      state.checkOrderSummary &&
      state.checkOrderSummary.id === action.payload.orderId
    ) {
      const { ticketId, ticketItemId, ticketItemStatus } = action.payload

      // Update ticket items status
      updateTicketItemsStatus(state, ticketId, [ticketItemId], ticketItemStatus)

      // Update ticket status to the minimum ticket item status
      updateTicketStatusFromItemsStatus(state, [ticketId])
    }
  },
}

export default ordersReducers
